/**
 * "Did you mean" for the free-text pickers.
 *
 * Asset name, brand, model and location are comboboxes precisely so the twentieth
 * Latitude 5440 is PICKED rather than retyped. That only works while the typing
 * matches: "Jio Dogal" shares no substring with the "Jio Dongle" already registered,
 * so the list came back empty and the create row was the only thing on offer. The
 * second spelling of one thing then exists forever, and no filter and no report ever
 * brings the two back together.
 *
 * A suggestion, never an autocorrect - see the note on `nearest`.
 */

/**
 * Levenshtein distance. Two rows rather than a full matrix: the lists here are the
 * distinct values already registered under one category, so a dozen strings at most.
 */
export function editDistance(a: string, b: string): number {
  // One row, updated in place, carrying the diagonal along by hand. The `?? 0` reads
  // as noise but every index here is provably in range - it is noUncheckedIndexedAccess
  // being satisfied, not a case that can happen.
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    let diagonal = i - 1;
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const above = row[j] ?? 0;
      const left = row[j - 1] ?? 0;
      row[j] = Math.min(above + 1, left + 1, diagonal + (a[i - 1] === b[j - 1] ? 0 : 1));
      diagonal = above;
    }
  }
  return row[b.length] ?? 0;
}

/**
 * The values closest to what was typed, nearest first.
 *
 * The budget scales with length, so a short word cannot match half the register:
 * "Dell" is two edits from "Bell" and they are different companies, while "Jio Dogal"
 * needs three to reach "Jio Dongle" and is plainly the same thing. Under three
 * characters nothing is offered at all - "M2" is near everything.
 *
 * Deliberately NOT a correction. A model number differs from its neighbour by one
 * character all day long - "Latitude 5440" and "Latitude 5450", "iPhone 13" and
 * "iPhone 15" - so a near match that substituted itself would invent a register
 * entry nobody typed. The caller offers these; the person picks one.
 */
export function nearest(labels: string[], query: string, limit = 3): string[] {
  const needle = query.trim().toLowerCase();
  if (needle.length < 3) return [];
  const budget = Math.max(1, Math.round(needle.length / 3));

  return labels
    .map((label) => ({ label, distance: editDistance(label.trim().toLowerCase(), needle) }))
    .filter((row) => row.distance <= budget)
    .sort((a, b) => a.distance - b.distance || a.label.localeCompare(b.label))
    .slice(0, limit)
    .map((row) => row.label);
}
